/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    IGroup,
    IGroupDelivery,
    IGroups
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-groups';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-order-information';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-sales-line';
import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/./order-confirmation';
import { IModuleProps, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import { getCartState } from '@msdyn365-commerce/global-state';

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {createdDate}
        {channelReferenceId}
        {receiptEmail}
    </Node>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    heading,
    count,
    processing,
    address,
    trackingInfo,
    pickupDateTimeslot
}) => (
    <Node {...deliveryProps}>
        {heading}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup> = ({ groupProps, delivery, salesLinesProps, salesLines }) => (
    <Node {...groupProps}>
        {delivery && <OrderConfirmationGroupDelivery {...delivery} />}
        {delivery && delivery.shippingItemsToYou}
        {salesLines && (
            <Node {...salesLinesProps}>
                {salesLines.map(salesLine => (
                    <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                ))}
            </Node>
        )}
    </Node>
);

export const OrderConfirmationGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group, index) => (
            <OrderConfirmationGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints,
    ...other
}) => {
    // @ts-ignore
    if (other.locale !== 'en-US' && other.subTotalAmount) {
        // @ts-ignore
        subtotal.props.value = other.subTotalAmount;
        // @ts-ignore
        if (other.shippingAmount) {
            // @ts-ignore
            shipping.props.value = other.shippingAmount;
        }
    }
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {subtotal}
            {shipping}
            {tax}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = ({
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    data,
    slots
}) => {
    const updateExtProps = async (modprops: IModuleProps) => {
        const currentCartState = await getCartState(modprops.moduleProps.context?.actionContext);
        const cartLines = currentCartState?.cart?.CartLines;
        if (!cartLines || (cartLines && cartLines.length === 0)) {
            console.log(currentCartState?.cart?.CartLines?.length);
            const removeExtProps = [
                {
                    Key: 'ExtPropsRemove_CAP',
                    Value: {
                        BooleanValue: true
                    }
                }
            ];
            await currentCartState.updateExtensionProperties({ newExtensionProperties: removeExtProps });
        }
    };

    React.useEffect(() => {
        void updateExtProps(moduleProps);
    }, []);
    const locale = moduleProps.moduleProps.context.request.locale;
    // @ts-ignore
    const salseOrderData = moduleProps?.moduleProps?.data?.orderHydration?.result?.salesOrder;
    const grossAmount = salseOrderData?.GrossAmount;
    const chargeAmount = salseOrderData?.ShippingChargeAmount;
    const subTotalAmount = grossAmount && chargeAmount ? (grossAmount - chargeAmount).toFixed(2) : '';
    const extraOrdersSummaryEu = {
        subTotalAmount: subTotalAmount,
        shippingAmount: chargeAmount,
        locale: locale
    };
    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />}
            {backToShoppingLink}
            {groups && <OrderConfirmationGroups {...groups} />}
            {payment && <OrderConfirmationPayment {...payment} />}
            {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} {...extraOrdersSummaryEu} />}
            {help && <OrderConfirmationHelp {...help} />}
            {slots &&
                slots.commisionJunction &&
                React.cloneElement(slots.commisionJunction[0] as React.ReactElement, {
                    salesOrder: data.orderHydration.result?.salesOrder
                })}
        </Module>
    );
};

export default OrderConfirmationView;
